import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent, CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;
  /** @type {number | null} */
  #syncTimeoutId = null;
  /** @type {boolean} */
  #externalSyncedOnce = false;

  connectedCallback() {
    // @ts-ignore - parent may or may not implement
    if (super.connectedCallback) super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAddForExternalDiscountSync);
  }

  disconnectedCallback() {
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAddForExternalDiscountSync);
    if (this.#syncTimeoutId) {
      clearTimeout(this.#syncTimeoutId);
      this.#syncTimeoutId = null;
    }
    // @ts-ignore
    if (super.disconnectedCallback) super.disconnectedCallback();
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * Handle cart add event and schedule a one-time external discount sync.
   * We wait a short delay to allow third-party apps (e.g. free gift / automatic discount) to finish cart updates.
   * This is intentionally minimal: we only refetch the discount section if new codes appear.
   */
  #handleCartAddForExternalDiscountSync = () => {
    // If user is actively applying/removing a code, skip automatic sync.
    if (this.#activeFetch) return;
    // Avoid repeated syncs for rapid successive adds (bundle adds, etc.). Allow one post-add sync per open session.
    if (this.#externalSyncedOnce) return;

    if (this.#syncTimeoutId) clearTimeout(this.#syncTimeoutId);
    this.#syncTimeoutId = window.setTimeout(() => {
      this.#syncTimeoutId = null;
      this.#externalSync();
    }, 500); // 500ms pragmatic delay; low risk & gives app time to apply discount
  };

  /**
   * Fetch updated section markup and morph if new discount codes are detected.
   */
  async #externalSync() {
    if (typeof this.dataset.sectionId !== 'string') return;
    const sectionId = this.dataset.sectionId;
    const before = this.#existingDiscounts();

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.sort();
      const response = await fetch(url.toString(), { method: 'GET', headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      const html = await response.text();
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const updatedSection = fragment.getElementById(`shopify-section-${sectionId}`);
      if (!updatedSection) return;
      const newCodes = Array.from(updatedSection.querySelectorAll('.cart-discount__pill'))
        .map((el) => (el instanceof HTMLLIElement ? el.dataset.discountCode : null))
        .filter(Boolean);

      const setsDiffer =
        newCodes.length !== before.length || newCodes.some((c) => !before.includes(/** @type {string} */ (c)));

      if (setsDiffer) {
        // Only morph the single section containing the discount snippet.
        morphSection(sectionId, updatedSection.outerHTML);
        this.#externalSyncedOnce = true; // prevent further automatic syncs this session (keeps it lightweight)
      }
    } catch (e) {
      // Swallow errors silently – non-blocking enhancement.
    }
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
